import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Play, Film, Zap, Award, ChevronDown } from "lucide-react";
import PageTransition from "../components/PageTransition";
import YouTubeModal from "../components/YouTubeModal";
import { Laurel } from "../components/Laurel";

const HERO_IMAGE = "/home/hero-vortex.png";
const CLOSING_IMAGE = "/home/filmmaker-portal.png";

/* ------------------ Cycling word ------------------ */
const CYCLE_WORDS = ["Direct.", "Create.", "Inspire.", "Produce."];
const CyclingWord = () => {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((p) => (p + 1) % CYCLE_WORDS.length), 1900);
    return () => clearInterval(t);
  }, []);
  return (
    <span
      className="relative inline-block align-baseline overflow-hidden"
      style={{ minWidth: "5.4ch", height: "1.05em", verticalAlign: "bottom" }}
      data-testid="hero-cycling-word"
    >
      <AnimatePresence mode="wait">
        <motion.span
          key={CYCLE_WORDS[i]}
          initial={{ y: "85%", opacity: 0 }}
          animate={{ y: "0%", opacity: 1 }}
          exit={{ y: "-85%", opacity: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 0.65, 0.2, 1] }}
          className="absolute left-0 top-0 italic text-gold whitespace-nowrap"
        >
          {CYCLE_WORDS[i]}
        </motion.span>
      </AnimatePresence>
    </span>
  );
};

/* ------------------ Floating film reel ------------------ */
const FilmReel = ({ className = "", duration = 40, reverse = false }) => (
  <motion.svg
    viewBox="0 0 200 200"
    aria-hidden="true"
    className={className}
    animate={{ rotate: reverse ? -360 : 360 }}
    transition={{ duration, repeat: Infinity, ease: "linear" }}
  >
    <circle cx="100" cy="100" r="96" fill="none" stroke="rgba(61,210,196,0.45)" strokeWidth="0.6" />
    <circle cx="100" cy="100" r="78" fill="none" stroke="rgba(61,210,196,0.35)" strokeWidth="0.6" />
    <circle cx="100" cy="100" r="60" fill="none" stroke="rgba(61,210,196,0.3)" strokeWidth="0.6" />
    <circle cx="100" cy="100" r="38" fill="none" stroke="rgba(61,210,196,0.5)" strokeWidth="0.8" />
    <circle cx="100" cy="100" r="6" fill="rgba(61,210,196,0.85)" />
    {/* Sprocket holes */}
    {Array.from({ length: 8 }).map((_, k) => {
      const a = (k * Math.PI * 2) / 8;
      const x = 100 + Math.cos(a) * 50;
      const y = 100 + Math.sin(a) * 50;
      return <circle key={k} cx={x} cy={y} r="3.2" fill="rgba(61,210,196,0.55)" />;
    })}
    {/* Outer tick marks */}
    {Array.from({ length: 36 }).map((_, k) => {
      const a = (k * Math.PI * 2) / 36;
      const x1 = 100 + Math.cos(a) * 92;
      const y1 = 100 + Math.sin(a) * 92;
      const x2 = 100 + Math.cos(a) * 96;
      const y2 = 100 + Math.sin(a) * 96;
      return <line key={`t${k}`} x1={x1} y1={y1} x2={x2} y2={y2} stroke="rgba(61,210,196,0.45)" strokeWidth="0.5" />;
    })}
  </motion.svg>
);

/* ------------------ Floating film frame strip ------------------ */
const FilmFrame = ({ className = "" }) => (
  <motion.svg
    viewBox="0 0 60 200"
    aria-hidden="true"
    className={className}
    animate={{ y: [0, -14, 0] }}
    transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
  >
    <rect x="0" y="0" width="60" height="200" fill="none" stroke="rgba(61,210,196,0.35)" strokeWidth="0.6" />
    {Array.from({ length: 5 }).map((_, k) => (
      <g key={k} transform={`translate(0,${k * 40})`}>
        <rect x="6" y="6" width="48" height="28" fill="rgba(61,210,196,0.06)" stroke="rgba(61,210,196,0.25)" strokeWidth="0.4" />
        <rect x="0" y="0" width="3" height="40" fill="rgba(61,210,196,0.35)" />
        <rect x="57" y="0" width="3" height="40" fill="rgba(61,210,196,0.35)" />
      </g>
    ))}
  </motion.svg>
);

/* ------------------ Hero ------------------ */
const Hero = () => {
  const ref = useRef(null);
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 800], [0, 220]);
  const scale = useTransform(scrollY, [0, 800], [1.02, 1.18]);
  const opacity = useTransform(scrollY, [0, 600], [1, 0.4]);

  return (
    <section
      ref={ref}
      data-testid="home-hero"
      className="relative h-screen min-h-[820px] w-full overflow-hidden bg-ink"
    >
      {/* "Looping video" placeholder: parallax image with slow Ken-Burns + scanlines + drifting ember/gold blobs + global grain (already on body) */}
      <motion.div
        style={{ y, scale }}
        className="absolute inset-0 will-change-transform"
        aria-hidden="true"
      >
        <motion.img
          src={HERO_IMAGE}
          alt=""
          loading="eager"
          decoding="async"
          fetchpriority="high"
          className="w-full h-[120%] object-cover"
          style={{ filter: "saturate(1.05) contrast(1.05) brightness(0.65)" }}
          animate={{ scale: [1, 1.07, 1] }}
          transition={{ duration: 24, repeat: Infinity, ease: "easeInOut" }}
        />
      </motion.div>

      {/* Drifting glow blobs to mimic ambient lighting */}
      <div className="cp-glow-blob cp-glow-blob--gold" style={{ top: "-8%", left: "-6%" }} />
      <div className="cp-glow-blob cp-glow-blob--ember" style={{ bottom: "-12%", right: "-8%" }} />

      {/* Soft gradient + vignette */}
      <div className="absolute inset-0 bg-gradient-to-b from-ink/55 via-ink/10 to-ink" />
      <div className="vignette" />
      <div className="cp-scanlines" />

      {/* Floating decorative film reels & frame strip */}
      <FilmReel
        className="hidden md:block absolute -top-24 -right-24 w-[28rem] h-[28rem] opacity-40 pointer-events-none"
        duration={48}
      />
      <FilmReel
        className="hidden md:block absolute -bottom-32 -left-28 w-[26rem] h-[26rem] opacity-25 pointer-events-none"
        duration={62}
        reverse
      />
      <FilmReel
        className="hidden lg:block absolute top-1/2 -translate-y-1/2 right-[8%] w-28 h-28 opacity-50 pointer-events-none"
        duration={32}
      />
      <FilmFrame className="hidden lg:block absolute top-1/2 -translate-y-1/2 left-[3.5%] w-12 h-64 opacity-50 pointer-events-none" />

      {/* Center content */}
      <motion.div style={{ opacity }} className="absolute inset-0 z-10 flex items-center">
        <div className="mx-auto max-w-[1500px] w-full px-6 md:px-12">
          <div className="max-w-5xl">
            {/* Cycling verb line */}
            <div className="overflow-hidden">
              <motion.div
                initial={{ y: "110%", opacity: 0 }}
                animate={{ y: "0%", opacity: 1 }}
                transition={{ duration: 0.9, delay: 0.35, ease: [0.22, 0.65, 0.2, 1] }}
                className="font-serif text-bone/85 leading-none mb-3 md:mb-5"
                style={{ fontSize: "clamp(1.6rem, 3.4vw, 3rem)" }}
              >
                We{" "}
                <CyclingWord />
              </motion.div>
            </div>

            {/* Main headline */}
            <h1
              className="font-serif text-bone leading-[0.95] tracking-tight"
              style={{ fontSize: "clamp(2.6rem, 8vw, 8.4rem)" }}
              data-testid="hero-headline"
            >
              <span className="block overflow-hidden">
                <motion.span
                  className="block"
                  initial={{ y: "110%", opacity: 0 }}
                  animate={{ y: "0%", opacity: 1 }}
                  transition={{ duration: 1.05, delay: 0.55, ease: [0.22, 0.65, 0.2, 1] }}
                >
                  Your Story.
                </motion.span>
              </span>
              <span className="block overflow-hidden">
                <motion.span
                  className="block"
                  initial={{ y: "110%", opacity: 0 }}
                  animate={{ y: "0%", opacity: 1 }}
                  transition={{ duration: 1.05, delay: 0.75, ease: [0.22, 0.65, 0.2, 1] }}
                >
                  Our Frames.
                </motion.span>
              </span>
              <span className="block overflow-hidden">
                <motion.span
                  className="block italic text-gold"
                  initial={{ y: "110%", opacity: 0 }}
                  animate={{ y: "0%", opacity: 1 }}
                  transition={{ duration: 1.05, delay: 0.95, ease: [0.22, 0.65, 0.2, 1] }}
                >
                  Infinite Possibilities.
                </motion.span>
              </span>
            </h1>

            {/* Subheadline */}
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.2 }}
              className="mt-8 text-bone/75 font-sans text-base md:text-xl leading-relaxed max-w-2xl"
              data-testid="hero-subheadline"
            >
              Cine Prompt is an independent AI filmmaking venture from Hyderabad with a vision
              to craft cinematic short films, brand stories and visual experiences powered by AI.
            </motion.p>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.3 }}
              className="mt-10 flex flex-wrap items-center gap-5"
            >
              <Link
                to="/work"
                className="cp-btn cp-btn--glow"
                data-testid="hero-cta-explore-work"
              >
                <Play size={12} /> Explore Our Work
              </Link>
              <Link
                to="/about"
                className="group inline-flex items-center gap-2 text-bone hover:text-gold transition-colors text-sm tracking-[0.22em] uppercase font-sans"
                data-testid="hero-cta-what-we-do"
              >
                Our Story
                <span className="inline-block transition-transform duration-500 group-hover:translate-x-1.5">→</span>
              </Link>
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* Scroll cue with slow pulse */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.8 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-3 text-bone/55"
        data-testid="hero-scroll-cue"
      >
        <span className="text-[10px] tracking-[0.35em] uppercase">Scroll</span>
        <span className="cp-scroll-pulse" />
        <ChevronDown size={14} className="text-gold/70" />
      </motion.div>
    </section>
  );
};

/* ------------------ Marquee ------------------ */
const MarqueeStrip = () => {
  const items = [
    "AI Short Films",
    "Brand Films",
    "Corporate Films",
    "Spec Ads",
    "AI Filmmaking",
    "Hyderabad",
    "Award-Winning",
  ];
  const repeated = [...items, ...items, ...items, ...items];
  return (
    <section className="relative bg-ink py-9 border-y border-gold/20 overflow-hidden" data-testid="home-marquee">
      <div className="cp-marquee-track">
        {repeated.map((it, i) => (
          <div key={`${it}-${i}`} className="flex items-center gap-10 px-6 text-bone/80">
            <span className="font-sans text-[11px] md:text-xs tracking-[0.34em] uppercase">{it}</span>
            <span className="w-1.5 h-1.5 rounded-full bg-gold" />
          </div>
        ))}
      </div>
    </section>
  );
};

/* ------------------ Feature Strip (3 col) ------------------ */
const FEATURES = [
  {
    icon: Film,
    title: "Story First, Always",
    desc:
      "We believe AI is the brush, not the painter. Every frame at Cine Prompt begins with a human story worth telling.",
  },
  {
    icon: Zap,
    title: "Built Without Boundaries",
    desc:
      "No crew. No studio. No limits. AI filmmaking puts the power of cinema into the hands of one passionate storyteller.",
  },
  {
    icon: Award,
    title: "Recognised & Celebrated",
    desc:
      "From IFFI Goa to Miami Art Tech Summit to the Mumbai International Film Festival — our films have been screened, awarded and celebrated on international stages.",
  },
];

const FeatureStrip = () => {
  return (
    <section className="relative bg-ink py-20 md:py-28" data-testid="home-feature-strip">
      <div className="mx-auto max-w-[1500px] px-6 md:px-12">
        <p className="overline mb-10" data-testid="feature-strip-overline">
          The Studio · How we make pictures
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.8, delay: i * 0.12, ease: [0.22, 0.65, 0.2, 1] }}
                data-testid={`feature-strip-item-${i + 1}`}
                className={`group relative px-2 md:px-10 py-10 md:py-4 ${
                  i !== 0 ? "md:border-l border-gold/20" : ""
                }`}
              >
                <div className="flex items-start gap-5">
                  <span className="inline-flex items-center justify-center w-12 h-12 border border-gold/40 text-gold shrink-0 group-hover:bg-gold group-hover:text-ink transition-colors duration-500">
                    <Icon size={20} strokeWidth={1.4} />
                  </span>
                  <div className="flex-1">
                    <p className="text-gold/80 font-sans text-[10px] tracking-[0.32em] uppercase mb-2">
                      {`0${i + 1}`}
                    </p>
                    <h3 className="font-serif text-bone text-2xl md:text-3xl leading-tight tracking-tight group-hover:text-gold transition-colors duration-500">
                      {f.title}
                    </h3>
                    <p className="mt-3 text-bone/65 font-sans text-sm leading-relaxed max-w-sm">
                      {f.desc}
                    </p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ------------------ Manifesto ------------------ */
const Manifesto = () => {
  return (
    <section className="relative bg-ink py-32 md:py-44" data-testid="home-manifesto">
      <div className="mx-auto max-w-[1300px] px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-3">
          <p className="overline mb-4">01 · Manifesto</p>
          <div className="hairline mb-6 max-w-[120px]" />
          <p className="font-sans text-bone/60 text-sm leading-relaxed">
            A note from the studio, written between renders.
          </p>
        </div>
        <div className="md:col-span-9">
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1, ease: [0.22, 0.65, 0.2, 1] }}
            className="font-serif text-bone leading-[1.05] tracking-tight"
            style={{ fontSize: "clamp(2rem, 5vw, 4.5rem)" }}
          >
            Technology should serve the story — <span className="italic text-gold">never</span>{" "}
            the other way around. AI gives us the power to produce films without compromise.
            But the <span className="italic text-gold">soul</span> of every film? That's
            entirely human.
          </motion.h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-10">
            <div>
              <p className="overline mb-3">Story</p>
              <p className="text-bone/70 text-sm leading-relaxed">
                AI is the brush. Never the painter. Every frame at Cine Prompt begins with a
                human story worth telling.
              </p>
            </div>
            <div>
              <p className="overline mb-3">Craft</p>
              <p className="text-bone/70 text-sm leading-relaxed">
                Lens choice. Grain. The fall-off of a key light at four in the morning. We
                treat synthesis like cinematography, not like a search bar.
              </p>
            </div>
            <div>
              <p className="overline mb-3">Soul</p>
              <p className="text-bone/70 text-sm leading-relaxed">
                Every voice, every score, every frame — touched by hand. The machine is fast.
                The story is what stays.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ------------------ Featured Work ------------------ */
const WorkTile = ({ item, large, tall, onPlay }) => {
  const hasVideo = Boolean(item.youtube);
  const commonClasses =
    "group block relative cp-frame border border-bone/5 w-full text-left overflow-hidden";
  const style = { aspectRatio: tall ? "3 / 4" : large ? "16 / 9" : "4 / 3" };

  const Inner = (
    <>
      <img loading="lazy" decoding="async" src={item.cover_image} alt={item.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.22,0.65,0.2,1)] group-hover:scale-[1.04]" />
      <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/15 to-transparent opacity-90 group-hover:opacity-70 transition-opacity duration-700" />

      {hasVideo && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-16 h-16 md:w-20 md:h-20 rounded-full border border-gold/60 bg-ink/40 backdrop-blur-sm inline-flex items-center justify-center opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
            <Play size={22} className="text-gold translate-x-[2px]" fill="currentColor" />
          </div>
        </div>
      )}

      {item.laurel && (
        <div className="absolute top-5 right-5 md:top-6 md:right-6" data-testid={`work-tile-${item.slug}-laurel`}>
          <Laurel label={item.laurel} />
        </div>
      )}

      <div className="absolute inset-0 p-6 md:p-8 flex flex-col justify-end">
        <div className="flex items-center gap-3 text-bone/60 text-[10px] tracking-[0.3em] uppercase mb-3">
          <span>{item.year}</span>
          <span className="w-1 h-1 rounded-full bg-gold" />
          <span>{item.category}</span>
        </div>
        <h3
          className="font-serif text-bone tracking-tight"
          style={{ fontSize: large ? "clamp(1.6rem, 3vw, 3rem)" : "clamp(1.4rem, 2vw, 2rem)" }}
        >
          {item.title}
        </h3>
        <p className="mt-2 text-bone/70 text-sm font-sans max-w-md hidden md:block">
          {item.summary}
        </p>
        <div className="mt-5 inline-flex items-center gap-2 text-gold text-[10px] tracking-[0.3em] uppercase opacity-80 group-hover:opacity-100">
          {hasVideo ? <>Watch film <Play size={11} fill="currentColor" /></> : <>Coming soon <ArrowUpRight size={12} /></>}
        </div>
      </div>
    </>
  );

  if (hasVideo) {
    return (
      <button
        type="button"
        onClick={() => onPlay(item)}
        data-testid={`work-tile-${item.slug}`}
        className={commonClasses + " cursor-pointer"}
        style={style}
        aria-label={`Play ${item.title}`}
      >
        {Inner}
      </button>
    );
  }

  return (
    <Link
      to={`/work`}
      data-testid={`work-tile-${item.slug}`}
      className={commonClasses}
      style={style}
    >
      {Inner}
    </Link>
  );
};

const FeaturedWork = ({ items }) => {
  const [playing, setPlaying] = useState(null);
  if (!items || items.length === 0) return null;
  // Up to 6 items in an asymmetric editorial grid
  const [a, b, c, d, e, f] = items;
  return (
    <section className="relative bg-ink py-24 md:py-36" data-testid="home-featured-work">
      <div className="mx-auto max-w-[1500px] px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-20">
          <div>
            <p className="overline mb-4">02 · Selected Work</p>
            <h2
              className="font-serif text-bone tracking-tight leading-[1]"
              style={{ fontSize: "clamp(2.4rem, 6vw, 6rem)" }}
            >
              Recent <span className="italic text-gold">apparitions</span>
            </h2>
          </div>
          <Link to="/work" className="cp-btn cp-btn--ghost self-start md:self-end" data-testid="featured-work-view-all">
            View archive <ArrowUpRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
          {a && (
            <div className="md:col-span-7 md:row-span-2">
              <WorkTile item={a} large onPlay={setPlaying} />
            </div>
          )}
          {b && (
            <div className="md:col-span-5">
              <WorkTile item={b} tall onPlay={setPlaying} />
            </div>
          )}
          {c && (
            <div className="md:col-span-5">
              <WorkTile item={c} onPlay={setPlaying} />
            </div>
          )}
          {d && (
            <div className="md:col-span-12">
              <WorkTile item={d} large onPlay={setPlaying} />
            </div>
          )}
          {e && (
            <div className="md:col-span-6">
              <WorkTile item={e} onPlay={setPlaying} />
            </div>
          )}
          {f && (
            <div className="md:col-span-6">
              <WorkTile item={f} onPlay={setPlaying} />
            </div>
          )}
        </div>
      </div>

      <AnimatePresence>
        {playing && (
          <YouTubeModal project={playing} onClose={() => setPlaying(null)} />
        )}
      </AnimatePresence>
    </section>
  );
};

/* ------------------ Stats / Numbers strip ------------------ */
const Stats = () => {
  const items = [
    { v: "9", l: "Films Produced" },
    { v: "4", l: "International Showcases" },
    { v: "2", l: "Hackathon Wins" },
    { v: "1", l: "Mission: Your Story" },
  ];
  return (
    <section className="relative bg-ink py-20 md:py-28 border-y border-gold/15" data-testid="home-stats">
      <div className="mx-auto max-w-[1500px] px-6 md:px-12 grid grid-cols-2 md:grid-cols-4 gap-10">
        {items.map((s, i) => (
          <motion.div
            key={s.l}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: i * 0.1 }}
          >
            <p className="overline mb-2">{`0${i + 1}`}</p>
            <p className="font-serif text-bone leading-none" style={{ fontSize: "clamp(2.6rem, 5vw, 5rem)" }}>
              {s.v}
            </p>
            <p className="mt-3 text-bone/55 text-sm font-sans">{s.l}</p>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

/* ------------------ Closing CTA ------------------ */
const ClosingCTA = () => (
  <section className="relative py-32 md:py-44 border-t border-gold/20 overflow-hidden" data-testid="home-closing-cta">
    {/* Filmmaker portal background */}
    <div className="absolute inset-0" aria-hidden="true">
      <img
        src={CLOSING_IMAGE}
        alt=""
        loading="lazy"
        decoding="async"
        className="w-full h-full object-cover"
        style={{ filter: "saturate(1.05) contrast(1.05) brightness(0.55)" }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-ink/60 via-ink/30 to-ink" />
      <div className="absolute inset-0 bg-ink/35" />
    </div>

    <div className="relative z-10 mx-auto max-w-[1300px] px-6 md:px-12 text-center">
      <p className="overline mb-6">Now booking · Brand films · Corporate · Spec ads</p>
      <h2
        className="font-serif text-bone tracking-tight leading-[0.98] mx-auto max-w-5xl"
        style={{ fontSize: "clamp(2.6rem, 8vw, 7rem)" }}
      >
        Have a story <br />
        worth <span className="italic text-gold">telling</span>?
      </h2>
      <Link to="/contact" className="cp-btn cp-btn--glow mt-10 inline-flex" data-testid="closing-cta-button">
        Start the Conversation <ArrowUpRight size={14} />
      </Link>
    </div>
  </section>
);

/* ------------------ Page ------------------ */
const FEATURED_WORK_DATA = [
  {
    slug: "the-legend-of-birsa-munda",
    title: "The Legend of Birsa Munda",
    category: "AI Short Film · Award Winner",
    year: "2025",
    client: "MIFF 2026 · IFFI 2025 · Miami · Budapest",
    summary: "Our most celebrated work — the story of India's tribal revolutionary, officially selected at the 19th Mumbai International Film Festival 2026 and screened across five international stages.",
    cover_image: "/work/birsa-munda-poster.jpg",
    youtube: "https://youtu.be/Bu2072Hixx4",
    laurel: "19th Mumbai International Film Festival 2026",
  },
  {
    slug: "lost-and-found",
    title: "Lost & Found",
    category: "Best Storyteller · CinemAI 2025",
    year: "2025",
    client: "Hackathon · IFFI 2025",
    summary: "48 hours, one story, one award.",
    cover_image: "/work/lost-and-found-poster.png",
    youtube: "https://youtu.be/BTAduL20x3I",
  },
  {
    slug: "arunima",
    title: "Arunima — The One Who Rose Above",
    category: "AI Short Film · True Story",
    year: "2026",
    client: "Independent Tribute",
    summary: "The true story of the first female amputee to conquer Mount Everest.",
    cover_image: "/work/arunima-poster.jpg",
    youtube: "https://youtu.be/qxLtYHYvV8Y",
  },
  {
    slug: "arc-spec-ad",
    title: "ARC — Outrun the Dark",
    category: "Spec Ad · Sports Brand",
    year: "2026",
    client: "Spec Advertisement",
    summary: "AI-native brand storytelling for the sports world — every frame engineered to move.",
    cover_image: "/work/arc-spec-ad-poster.png",
    youtube: "https://youtu.be/U29bvEiAm50",
  },
  {
    slug: "baraat",
    title: "Baraat",
    category: "AI Short Film · Official Selection",
    year: "2026",
    client: "NOMOREFROG AI Film Awards · South Korea",
    summary: "Some baraats never reach their destination. Officially selected at the NOMOREFROG AI Film Awards, South Korea.",
    cover_image: "/work/baraat-poster.jpg",
    youtube: "https://youtu.be/mIIXUCY3Ifg",
    laurel: "NOMOREFROG AI Film Awards · S.Korea",
  },
  {
    slug: "viraam",
    title: "Viraam",
    category: "AI Short Film · Created for Higgsfield AI Film Festival",
    year: "2026",
    client: "Higgsfield AI Film Festival Entry",
    summary: "A moment. A memory. A war within. Some battles never end — they just pause.",
    cover_image: "/work/viraam-poster.jpg",
    youtube: "https://youtu.be/exKcpUoDXW4",
  },
];

const Home = () => {
  return (
    <PageTransition testId="page-home">
      <Hero />
      <MarqueeStrip />
      <FeatureStrip />
      <Manifesto />
      <FeaturedWork items={FEATURED_WORK_DATA} />
      <Stats />
      <ClosingCTA />
    </PageTransition>
  );
};

export default Home;
